import Layout from '../../components/Layout';
import { Outlet, useOutletContext } from "react-router-dom";
import CustomStepper from "../../components/CustomStepper";
import { goBack, push } from "redux-first-history";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import LineLoader from "../../components/Loaders/LineLoader/LineLoader";
import { RootState } from "../../store/index";
import { HTTPRequestStatus } from "../../utils/models";
import { finalBalanceRouteMap, finalBalanceRouteNames } from "../../store/reducer/payment";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../components/ErrorFallback";
import { SuccessAlert, ErrorAlert } from "../../components/Alerts";
import { useEffect } from "react";
import { paymentSlice } from "../../store/reducer/payment";
import { TaxCalculations } from "../../model/tax_calculations";
import { isTaxReturnAwaitingPayment, isTaxReturnAwaitingRefund } from '../../hooks/TaxReturnState';

type ContextType = { calculations: TaxCalculations };

function GenericAlert({ status, message }: { status: HTTPRequestStatus, message: string }) {
  if (status === HTTPRequestStatus.Error) {
    return <ErrorAlert>{message}</ErrorAlert>
  } else if (status === HTTPRequestStatus.Success && message !== '') {
    return <SuccessAlert message={message}/>
  } else {
    return null;
  }
}

const steps = ["summary", "review balance", "signature"];

/*
 * Main checkpoint of this container is signing the tax return
 * TODO important: this container should always fetch final balance
 * from endpoint. Not 100% sure they will get loaded if user logs in and out
 * during this stage
 */
const FinalBalanceContainer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    alertMessage,
    finalBalance,
    status,
  } = useSelector((state: RootState) => state.payment);
  const { frontendState: { returnSigned } } = useSelector((state: RootState) => state.state);
  const isLoading = status === HTTPRequestStatus.Requesting;
  const needsPayment = isTaxReturnAwaitingPayment();
  const needsRefund = isTaxReturnAwaitingRefund();

  if (returnSigned || needsPayment || needsRefund) {
    dispatch(push('/transition/post-signature'));
  }

  useEffect(() => {
    let ignore = false;

    if (!ignore) {
      dispatch(paymentSlice.actions.finalBalanceRequest());
    }

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    document.title = import.meta.env.VITE_APP_NAME + ' - ' + finalBalanceRouteNames[finalBalanceRouteMap[location.pathname]];
  }, [location]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
    <Layout>
      <CustomStepper
        steps={steps}
        activeStep={finalBalanceRouteMap[location.pathname]}
        handleBack={() => dispatch(goBack())}
        showBackButton={false}
        hideNextButton={true}>
        {isLoading && <LineLoader />}
        <GenericAlert
          message={alertMessage}
          status={status}
        />
        <Outlet context={{calculations: finalBalance}}/>
      </CustomStepper>
    </Layout>
    </ErrorBoundary>
  );
};

export default FinalBalanceContainer;

export function useFinalBalanceContext() {
  return useOutletContext<ContextType>();
}
